import React from 'react';
import Modal from '../../../atoms/modal/Modal';
import ModalTitle from '../../../atoms/modal/ModalTitle';
import ModalBody from '../../../atoms/modal/ModalBody';
import ModalActions from '../../../atoms/modal/ModalActions';
import FormLabelInput from '../../../molecules/form/FormLabelInput';
import FormLabelDropdown from '../../../molecules/form/FormLabelDropdown';
import FormWrapper from '../../../molecules/form/FormWrapper';
import Button from '../../../atoms/button/Button';
import { useForm } from 'react-hook-form';
import { IOrganizationalImpactFormData } from '../../../commons/types';
import { useAppSelector } from '../../../hooks/redux';
import { useActions } from '../../../hooks/actions';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../routes/routes';
import { IAddClientStatementRequest } from '../../../store/api/types/leadership';
import {
  useAddClientStatementMutation,
  useLazyGetExpertiseCategoryQuery,
  useLazyGetImpactModuleQuery,
  useUpdateClientStatementMutation,
} from '../../../store/api/leadership';

export default function OrganizationalImpactSLModal({
  clientId,
}: {
  clientId?: string;
}) {
  const [getClientImpactModule, { data: clientImpactModuleData }] =
    useLazyGetImpactModuleQuery();
  const [getClientExpertiseCategory, { data: clientExpertiseCategory }] =
    useLazyGetExpertiseCategoryQuery();

  // To get created statement ID  and navigate
  const navigate = useNavigate();

  const [addClientStatement] = useAddClientStatementMutation();
  const [editClientStatement] = useUpdateClientStatementMutation();

  const initialValues = useAppSelector(
    (state) => state.organizationalImpactSLModal.initialValues
  );
  const isOpen = useAppSelector(
    (state) => state.organizationalImpactSLModal.isOpen
  );
  const modalType = useAppSelector(
    (state) => state.organizationalImpactSLModal.modalType
  );

  const {
    toggleErrorSnackbar,
    toggleSuccessSnackbar,
    closeOrganizationalImpactSLModal,
  } = useActions();

  const methods = useForm<IOrganizationalImpactFormData>();
  const {
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = methods;

  const handleClose = () => {
    closeOrganizationalImpactSLModal();
  };

  const clientSubmitHandler = async (
    requestPayload: IAddClientStatementRequest
  ) => {
    const modalTypeMap = {
      edit: {
        fn: editClientStatement,
        messageSuccess: 'Statement Updated Successfully',
        errorMessage: 'Error Updating Statement',
      },
      add: {
        fn: addClientStatement,
        messageSuccess: 'Statement Added Successfully',
        errorMessage: 'Error Adding Statement',
      },
    };

    try {
      const response = await modalTypeMap[modalType]
        .fn(requestPayload)
        .unwrap();
      if (response?.status === 'success' && response?.code === 200) {
        toggleSuccessSnackbar({
          message: modalTypeMap[modalType].messageSuccess,
        });
        handleClose();

        if (modalType === 'add') {
          if (clientId) {
            // redirect to client statement details screen
            navigate(
              `${routes.company.url}${clientId}${routes.company.statements.url}/${response?.data?.id}`
            );
          } else {
            // redirect master statement details
            navigate(
              `${routes.statementLibrary.url}/${response?.data?.id}${routes.statementLibrary.details.url}`
            );
          }
        }
      }
      if (response?.message) {
        toggleErrorSnackbar({ message: response.message });
      }
    } catch (e) {
      const error = e as {
        data?: { detail: string };
        status?: number;
        message?: string;
      };
      if (error.data?.detail)
        toggleErrorSnackbar({ message: error.data.detail });
      else if (error.message) toggleErrorSnackbar({ message: error.message });
      else
        toggleErrorSnackbar({ message: modalTypeMap[modalType].errorMessage });
    }
  };

  React.useEffect(() => {
    reset(initialValues);
  }, [isOpen, reset, initialValues]);

  React.useEffect(() => {
    if (clientId && !clientExpertiseCategory) {
      getClientExpertiseCategory(clientId);
    } else {
      getClientExpertiseCategory('Master');
    }

    if (clientId && !clientImpactModuleData) {
      getClientImpactModule(clientId);
    } else {
      getClientImpactModule('Master');
    }
  }, [setValue]);

  const onSubmit = (data: IOrganizationalImpactFormData) => {
    const client = clientId ? clientId : 'Master';
    const requestPayload: IAddClientStatementRequest = {
      params: {
        id: initialValues.id,
        clientId: client,
        skillGroup: 'Organizational Impact',
      },
      payload: {
        name: data.name,
        description: data.description,
        impactModuleCode: data.impact_area,
        expertiseCategoryCode: data.leadership_acumen,
      },
    };

    clientSubmitHandler(requestPayload);
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      disableEscapeKeyDown={true}
    >
      <ModalTitle onClose={handleClose}>
        {modalType === 'add'
          ? 'Add a new organizational impact statement'
          : 'Edit Statement'}
      </ModalTitle>
      <ModalBody>
        <FormWrapper
          methods={methods}
          id='add-new-role'
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormLabelDropdown
            name='impact_area'
            placeholder='Select impact module'
            rules={{ required: 'Please select an impact module' }}
            label='Impact module'
            tooltip='tooltip info'
            dropDownItem={clientImpactModuleData?.dropdownList ?? []}
            defaultValue={
              initialValues.impact_area ? initialValues.impact_area : ''
            }
          />
          <FormLabelInput
            name='name'
            rules={{ required: 'Please provide a statement name' }}
            label='Statement name'
            tooltip='tooltip info'
          />
          <FormLabelInput
            name='description'
            rules={{ required: 'Please provide a description ' }}
            label='Description'
            tooltip='Statement description'
            extraLabelInputProps={{
              minRows: 2,
              maxRows: 2,
              multiline: true,
            }}
          />

          <FormLabelDropdown
            name='leadership_acumen'
            placeholder='Select leadership expertise'
            rules={{ required: 'Please select a Leadership expertise' }}
            label='Leadership expertise'
            tooltip='tooltip info'
            dropDownItem={clientExpertiseCategory?.dropdownList ?? []}
            defaultValue={
              initialValues.leadership_acumen
                ? initialValues.leadership_acumen
                : ''
            }
          />
        </FormWrapper>
      </ModalBody>
      <ModalActions>
        <Button
          btnType={'PRIMARY'}
          text={'Save'}
          type='submit'
          form='add-new-role'
          disabled={!!Object.values(errors).length}
        />
        <Button
          btnType={'SECONDARY'}
          text={'Cancel'}
          onClick={handleClose}
        />
      </ModalActions>
    </Modal>
  );
}
